import React, { useState, useRef, useEffect } from 'react';
import { Paper, Typography, IconButton, Box, CircularProgress, Collapse } from '@material-ui/core';
import { Close, CheckCircle, Error, DragIndicator } from '@material-ui/icons';

import { useTranslation } from 'react-i18next';

function ImportStatusModal({
  isOpen,
  status,
  title,
  message,
  errors = [],
  onClose,
}) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 80, y: 80 });
  const paperRef = useRef(null);

  const getBackgroundColor = () => {
    switch (status) {
      case 'loading':
        return 'rgba(0, 0, 0, 0.9)';
      case 'success':
        return '#0dab9b';
      case 'error':
        return '#e57373';
      default:
        return 'rgba(0, 0, 0, 0.9)';
    }
  };

  // Manejar inicio del arrastre
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = paperRef.current.getBoundingClientRect();
    const rightDistance = window.innerWidth - rect.right;

    setDragStart({
      x: e.clientX + rightDistance,
      y: e.clientY - position.y,
    });
  };

  // Manejar movimiento del mouse
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: dragStart.x - e.clientX,
      y: e.clientY - dragStart.y + position.y,
    });
  };

  // Terminar arrastre
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart.x, dragStart.y]);

  if (!isOpen) return null;

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box display="flex" alignItems="center">
            <Box mr={1.25}>
              {' '}
              <CircularProgress size={16} style={{ color: '#fff' }} />
            </Box>
            <Typography variant="body2" style={{ fontSize: '13px', color: '#fff' }}>
              {title || t('importStatus.title')}...
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box display="flex" alignItems="center">
            <CheckCircle style={{ color: '#fff', fontSize: 18, paddingRight: '10px' }} />
            <Typography variant="body2" style={{ fontSize: '13px', color: '#fff' }}>
              {message || t('importStatus.success')}
            </Typography>
          </Box>
        );

      case 'error':
        return (
          <Box>
            <Box display="flex" alignItems="center" mb={errors.length > 0 ? 1 : 0}>
              <Error style={{ color: '#fff', fontSize: 18, paddingRight: '10px' }} />
              <Typography variant="body2" style={{ fontSize: '13px', color: '#fff' }}>
                {message || t('importStatus.error')}
              </Typography>
            </Box>

            {errors.length > 0 && (
              <Box mt={1}>
                <Typography
                  variant="caption"
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '11px',
                    color: 'rgba(255, 255, 255, 0.8)',
                  }}
                  onClick={() => setShowDetails(!showDetails)}>
                  {showDetails ? t('importStatus.hideErrors') : t('importStatus.showErrors', { count: errors.length })}
                </Typography>

                <Collapse in={showDetails}>
                  <Box mt={1} style={{ maxHeight: 120, overflow: 'auto' }}>
                    {errors.slice(0, 3).map((error, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        style={{
                          display: 'block',
                          fontSize: '10px',
                          color: 'rgba(255, 255, 255, 0.7)',
                        }}>
                        • {error.rut}: {error.message}
                      </Typography>
                    ))}
                    {errors.length > 3 && (
                      <Typography
                        variant="caption"
                        style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.5)' }}>
                        {t('importStatus.andMore', { count: errors.length - 3 })}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper
      ref={paperRef}
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        right: `${position.x}px`,
        backgroundColor: getBackgroundColor(),
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        minWidth: '280px',
        maxWidth: '400px',
        zIndex: 9999,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      elevation={8}
      onMouseDown={handleMouseDown}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <DragIndicator
            style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: 16,
              cursor: 'grab',
            }}
          />
          <Box flex={1} marginLeft={2}>
            {renderContent()}
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          style={{
            color: 'white',
            marginLeft: '8px',
            marginTop: '-4px',
            padding: '4px',
          }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

export default ImportStatusModal;
